import { useState, useEffect } from 'react';
import { Grid, Radio, RadioGroup, FormControlLabel, Modal, CircularProgress, FormHelperText } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import showToast from 'components/Toast/Toast';
import { requestScriptDify } from 'utils/requests/dify';
import { editWeekPost } from 'utils/requests/communities';
import * as S from '../forms/style';
import Autocomplete from '@mui/material/Autocomplete';
import { useFormik } from 'formik';
import * as Yup from "yup";

const formatOptions = [
  { value: 'video', label: 'Vídeo' },
  { value: 'reel', label: 'Reel' },
  { value: 'story', label: 'Story' },
  { value: 'post', label: 'Post' },
  { value: 'carousel', label: 'Carrossel' }
];

const BuildScriptForm = ({ openModal, closeModal, contentOptions, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [validateFields, setValidateFields] = useState(false);

  useEffect(() => {
    if (openModal) {
      formik.resetForm();
      setValidateFields(false);
    }
  }, [openModal]);

  const formik = useFormik({
    initialValues: {
      contentId: '',
      format: '',
      objective: '',
      observation: ''
    },

    validateOnChange: validateFields,
    validateOnBlur: false,

    validationSchema: Yup.object({
      contentId: Yup.string().required('O conteúdo é obrigatório'),
      format: Yup.string().required('O formato é obrigatório'),
      objective: Yup.string().required('O objetivo é obrigatório'),
      observation: Yup.string()
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const selectedContent = contentOptions.find(option => option.id === values.contentId);
        if (!selectedContent) {
          showToast("Conteúdo não encontrado", "error");
          return;
        };
        const scriptFormData = {
          contentDescription: selectedContent.description,
          formatText: getFormatName(values.format),
          objectiveText: values.objective,
          weekNumber: selectedContent.label.match(/Semana (\d+)/)?.[1] || '1',
          postNumber: selectedContent.label.match(/Post (\d+)/)?.[1] || '1',
          postType: 'Post regular',
          observation: values.observation
        };
        const response = await requestScriptDify(scriptFormData);
        if (response && response.answer) {
          const newScript = {
            content: response.answer,
            format: values.format,
            objective: values.objective,
            observation: values.observation,
            createdAt: new Date().toISOString(),
          };

          const updatedScriptData = {
            tone: selectedContent.tone,
            title: selectedContent.post_title,
            format: selectedContent.format,
            objective: selectedContent.objective,
            description: selectedContent.description,
            generated_script: newScript
          };

          const updateResponse = await editWeekPost(values.contentId, {
            script_data: JSON.stringify(updatedScriptData)
          });

          if (updateResponse) {
            showToast("Roteiro gerado e salvo com sucesso!", "success");
            closeModal();
            refetch();
          } else {
            showToast("Erro ao salvar roteiro", "error");
          }
        } else {
          const errorMsg = response?.rawMessage[0]?.split(': ')[1] || response?.message;
          showToast(errorMsg ? `Erro ao gerar roteiro: ${errorMsg}` : "Erro ao gerar roteiro", "error");
        }
      } catch (error) {
        const errorMsg = response?.rawMessage[0]?.split(': ')[1] || response?.message;
          showToast(errorMsg ? `Erro ao gerar roteiro: ${errorMsg}` : "Erro ao gerar roteiro", "error");
      } finally {
        setLoading(false);
      }
    }
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);
    setValidateFields(true);
  };

  const getFormatName = (format) => {
    const formatOption = formatOptions.find(option => option.value === format);
    return formatOption ? formatOption.label : format;
  };

  return (
    <Modal
      open={openModal}
      onClose={closeModal}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <S.ModalContainer>
        <S.CloseButton
          aria-label="close"
          onClick={closeModal}
        >
          <CloseIcon />
        </S.CloseButton>

        <S.ModalTitle>
          <S.ModalTitleText variant="h5">
            Criar Novo Roteiro
          </S.ModalTitleText>
        </S.ModalTitle>

        <S.ModalContent>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Qual o tema do seu conteúdo?
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12} md={12} xl={12}>
              <Autocomplete
                limitTags={10}
                disablePortal
                name="contentId"
                value={contentOptions.find(option => option.id === formik.values.contentId) || null}
                onChange={(e, value) => {
                  handleChange({
                    target: {
                      name: 'contentId',
                      value: value ? value.id : ''
                    }
                  });
                }}
                options={contentOptions}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option.label}
                noOptionsText="Todos os conteúdos já possuem roteiros"
                renderInput={(params) =>
                  <S.InputBuildScript {...params} 
                  placeholder={contentOptions.length === 0 ? 'Nenhum conteúdo disponível' : 'Selecione o tema do seu conteúdo'} />
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </S.InputGridBuildScript>
            {formik.touched.contentId && formik.errors.contentId && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.contentId}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Qual o formato que você quer usar ?
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12} md={12} xl={12}>
              <Autocomplete
  limitTags={10}
                disablePortal
                name="format"
                value={formatOptions.find(option => option.value === formik.values.format) || null}
                onChange={(e, value) => {
                  handleChange({
                    target: {
                      name: 'format',
                      value: value ? value.value : ''
                    }
                  });
                }}
                options={formatOptions}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option.label}
                renderInput={(params) =>
                  <S.InputBuildScript {...params} 
                  placeholder='Selecione o formato desejado' />
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </S.InputGridBuildScript>
            {formik.touched.format && formik.errors.format && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.format}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.RadioGroupContainer>
            <S.SectionTitle variant="h6">
              Qual o seu objetivo com esse conteúdo?
            </S.SectionTitle>
            <RadioGroup
              row
              name="objective"
              value={formik.values.objective}
              onChange={handleChange}
            >
              <Grid container>
                <FormControlLabel value="engage" control={<Radio />} label="Engajar meu público" />
                <FormControlLabel value="promote" control={<Radio />} label="Divulgar um produto ou curso" />
                <FormControlLabel value="inspire" control={<Radio />} label="Inspirar com algo pessoal" />
                <FormControlLabel value="response" control={<Radio />} label="Gerar comentário ou resposta" />
                <FormControlLabel value="other" control={<Radio />} label="Outro" />
              </Grid>
            </RadioGroup>
            {formik.touched.objective && formik.errors.objective && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.objective}
              </FormHelperText>
            )}
          </S.RadioGroupContainer>
          <Grid container sx={{ marginTop: '20px' }}>
            <S.SectionTitle variant="h6">
              Quer adicionar alguma observação?
            </S.SectionTitle>
            <S.InputGridBuildScript item md={12} xl={12}>
              <S.Input
                hiddenLabel
                name='observation'
                type='text'
                placeholder="Descreva (opcional)"
                fullWidth
                value={formik.values.observation}
                onChange={handleChange}
              />
            </S.InputGridBuildScript>
          </Grid>
        </S.ModalContent>
        <S.ButtonContainer>
          <S.EditButton
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={loading || contentOptions.length === 0}
          >
            {loading && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
            {loading ? 'Gerando...' : contentOptions.length === 0 ? 'Nenhum conteúdo disponível' : 'Gerar Meu Roteiro'}
          </S.EditButton>
        </S.ButtonContainer>
      </S.ModalContainer>
    </Modal>
  );
};

export default BuildScriptForm; 